import type React from "react";
import { cn } from "@/lib/utils";

/**
 * The mark: a 5x7 dot-matrix "S" on an ink tile. Its fifteen dots are lit along the
 * stroke, from the laser spot (paper) through the lilac → sky dispersion.
 * public/favicon.svg and the app icons are rendered from this same geometry.
 */
const STROKE: readonly (readonly [number, number])[] = [
	[4, 0],
	[3, 0],
	[2, 0],
	[1, 0],
	[0, 1],
	[0, 2],
	[1, 3],
	[2, 3],
	[3, 3],
	[4, 4],
	[4, 5],
	[3, 6],
	[2, 6],
	[1, 6],
	[0, 6],
];

const LIT = new Set(STROKE.map(([x, y]) => `${x},${y}`));
const UNLIT = Array.from({ length: 35 }, (_, i) => [i % 5, Math.floor(i / 5)])
	.filter(([x, y]) => !LIT.has(`${x},${y}`))
	.map(([x, y]) => [cx(x), cy(y)] as const);

function cx(x: number) {
	return 18 + x * 7;
}

function cy(y: number) {
	return 11 + y * 7;
}

/** Laser spot, then lilac-ink lifted toward the pastels, then sky. */
const FROM = [0xb0, 0xa8, 0xf0];
const TO = [0x7f, 0xc0, 0xdc];
function dotColor(i: number) {
	if (i === 0) return "var(--paper)";
	const t = (i - 1) / (STROKE.length - 2);
	const [r, g, b] = FROM.map((from, c) =>
		Math.round(from + ((TO[c] ?? from) - from) * t),
	);
	return `rgb(${r} ${g} ${b})`;
}

export function LogoMark({
	className,
	print = false,
	delay = 0,
}: {
	className?: string;
	/** Strike the dots in stroke order on first paint (CSS, before hydration). */
	print?: boolean;
	/** Seconds before the first dot is struck. */
	delay?: number;
}) {
	return (
		<svg
			viewBox="0 0 64 64"
			className={cn(
				"logo-mark shrink-0",
				print && "logo-mark--print",
				className,
			)}
			style={{ "--d": `${delay}s` } as React.CSSProperties}
			aria-hidden="true"
		>
			<rect width="64" height="64" rx="14" style={{ fill: "var(--ink)" }} />
			{UNLIT.map(([x, y]) => (
				<circle
					key={`${x}-${y}`}
					cx={x}
					cy={y}
					r="1"
					style={{ fill: "var(--tone-2)" }}
				/>
			))}
			{STROKE.map(([x, y], i) => (
				<g key={`${x}-${y}`} style={{ "--i": i } as React.CSSProperties}>
					<circle
						className="logo-dot"
						cx={cx(x)}
						cy={cy(y)}
						r="2.95"
						style={{ fill: dotColor(i) }}
					/>
					<circle
						className="logo-glow"
						cx={cx(x)}
						cy={cy(y)}
						r="2.95"
						style={{ fill: "var(--paper)" }}
					/>
				</g>
			))}
		</svg>
	);
}
